import * as React from 'react'
// import cx from 'classnames'
// import Link from 'next/link'
// import {useRouter} from 'next/router'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { useSettingBreadcrumbs } from 'features/Setting/useSettingBreadcrumbs'
import { EXTERNAL_PATH } from 'lib/paths'
import { toggleDefaultConfirmBox } from 'store/actions/confirmBox'
import {
  useCurrentUser,
  useLazyUsersSecretQuery,
  useUsersSecretDisableMutation,
  useUsersSecretEnableMutation,
} from 'store/api/yay/user'
import withSettingModal, {
  WithSettingModalProps,
} from 'features/Setting/withSettingModal'

import Button from 'components/Button'
import { CodeInput, SettingItem, Switch } from 'components'

import '../style.scss'

export const CODE_LENGTH = 6

enum STEP {
  INIT,
  NOT_ENABLED_YET,
  QR,
  CODE,
  TURN_ON_SUCCESS,
  TURNED_ON,
  TURN_OFF_SUCCESS,
}

const TwoFaSetting = ({ setTitle }: WithSettingModalProps) => {
  const { t } = useTranslation()
  const currentUser = useCurrentUser()
  const dispatch = useDispatch()

  const [step, setStep] = React.useState(STEP.INIT)
  const [code, setCode] = React.useState('')
  const [errorText, setErrorText] = React.useState('')

  const pageTitle = t('user_setting.2fa.title')
  React.useEffect(() => setTitle(pageTitle), [])
  useSettingBreadcrumbs([{ title: pageTitle }])

  const [usersSecret, usersSecretRes] = useLazyUsersSecretQuery()
  const [usersSecretEnable, usersSecretEnableRes] =
    useUsersSecretEnableMutation()
  const [usersSecretDisable, usersSecretDisableRes] =
    useUsersSecretDisableMutation()

  const isTwoFaEnabled = currentUser?.data?.two_fa_enabled

  React.useEffect(() => {
    if (currentUser.isSuccess && step == STEP.INIT) {
      if (isTwoFaEnabled) {
        setStep(STEP.TURNED_ON)
      } else {
        setStep(STEP.NOT_ENABLED_YET)
      }
    }
  }, [currentUser, step, isTwoFaEnabled])

  return (
    <React.Fragment>
      {step == STEP.NOT_ENABLED_YET && (
        <div className="UserSetting">
          <div className="UserSetting__header">
            <h3 className="UserSetting__h3">
              {t('user_setting.2fa.not_enabled_yet.h3')}
            </h3>

            <p className="UserSetting__p">
              {t('user_setting.2fa.not_enabled_yet.p')}
            </p>
          </div>

          <ButtonWrapper
            onClick={async () => {
              await usersSecret()
              setStep(STEP.QR)
            }}
            disabled={usersSecretRes?.isFetching}
            isLoading={usersSecretRes?.isFetching}
          />
        </div>
      )}

      {step == STEP.QR && (
        <div className="UserSetting">
          <div className="TwoFaSetting">
            <div className="TwoFaSetting__box">
              <span className="TwoFaSetting__box__step">
                {t('user_setting.2fa.qr.step', { step: 1 })}
              </span>

              <h4 className="TwoFaSetting__box__h4">
                {t('user_setting.2fa.qr.0.h4')}
              </h4>

              <p className="TwoFaSetting__box__p">
                {t('user_setting.2fa.qr.0.p')}
              </p>
            </div>

            <div className="TwoFaSetting__box">
              <span className="TwoFaSetting__box__step">
                {t('user_setting.2fa.qr.step', { step: 2 })}
              </span>

              <h4 className="TwoFaSetting__box__h4">
                {t('user_setting.2fa.qr.1.h4')}
              </h4>

              <div className="TwoFaSetting__box__image">
                <img
                  className="TwoFaSetting__box__image__img"
                  src={usersSecretRes?.data?.qr_code}
                />
              </div>

              <p className="TwoFaSetting__box__p">
                {t('user_setting.2fa.qr.1.p')}
              </p>

              <p className="TwoFaSetting__box__code">
                {usersSecretRes?.data?.secret}
              </p>
            </div>

            <div className="TwoFaSetting__box">
              <span className="TwoFaSetting__box__step">
                {t('user_setting.2fa.qr.step', { step: 3 })}
              </span>

              <h4 className="TwoFaSetting__box__h4">
                {t('user_setting.2fa.qr.2.h4')}
              </h4>
            </div>
          </div>

          <ButtonWrapper
            onClick={() => {
              setStep(STEP.CODE)
            }}
          />
        </div>
      )}

      {step == STEP.CODE && (
        <form
          className="UserSetting"
          onSubmit={async (e) => {
            e.preventDefault()

            if (isTwoFaEnabled) {
              const _usersSecretDisableRes = await usersSecretDisable({ code })

              if ('error' in _usersSecretDisableRes) {
                setErrorText(t('user_setting.2fa.code.error.incorrect'))
              } else if ('data' in _usersSecretDisableRes) {
                setStep(STEP.TURN_OFF_SUCCESS)
              }
            } else {
              const _usersSecretEnableRes = await usersSecretEnable({ code })

              if ('error' in _usersSecretEnableRes) {
                setErrorText(t('user_setting.2fa.code.error.incorrect'))
              } else if ('data' in _usersSecretEnableRes) {
                setStep(STEP.TURN_ON_SUCCESS)
              }
            }
          }}
        >
          <div className="UserSetting__code-verification">
            <h3 className="UserSetting__code-verification__h3">
              {t('user_setting.2fa.code.h3')}
            </h3>

            <p className="UserSetting__code-verification__p">
              {t('user_setting.2fa.code.p')}
            </p>

            <div className="UserSetting__code-verification__code">
              <CodeInput
                value={code}
                setValue={setCode}
                length={CODE_LENGTH}
                error={errorText}
                clearError={() => setErrorText('')}
              />
            </div>

            <p className="UserSetting__code-verification__p">
              <a
                className="UserSetting__code-verification__a UserSetting__code-verification__a--external"
                href={EXTERNAL_PATH.TWO_FA()}
                target="_blank"
                rel="nofollow noopener noreferrer"
              >
                {t('user_setting.2fa.code.a')}
              </a>
            </p>
          </div>

          <ButtonWrapper
            disabled={
              usersSecretEnableRes?.isLoading ||
              usersSecretDisableRes?.isLoading ||
              code?.length < CODE_LENGTH
            }
            isLoading={
              usersSecretEnableRes?.isLoading ||
              usersSecretDisableRes?.isLoading
            }
          />
        </form>
      )}

      {step == STEP.TURN_ON_SUCCESS && (
        <div className="UserSetting">
          {(() => {
            const _codes = usersSecretEnableRes?.data?.recovery_codes

            return (
              <div className="UserSetting__success">
                <img
                  className="UserSetting__success__img"
                  src="/images/success_check.png"
                  alt="success"
                />

                <h3 className="UserSetting__h3">
                  {t('user_setting.2fa.success.h3')}
                </h3>

                <p className="UserSetting__p">
                  {t('user_setting.2fa.success.p')}
                </p>

                <div className="UserSetting__success__codes">
                  {_codes?.map?.((x, i) => (
                    <p className="UserSetting__success__codes__p" key={i}>
                      {x}
                    </p>
                  ))}
                </div>

                <div className="UserSetting__success__buttons">
                  <Copy codes={_codes} />
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {step == STEP.TURNED_ON && (
        <SettingItem
          label={t('user_setting.2fa.title')}
          sub={t('user_setting.2fa.success.sub')}
          noBorder
        >
          <Switch
            checked
            onClick={() => {
              dispatch(
                toggleDefaultConfirmBox({
                  title: t('user_setting.2fa.success.confirm.title'),
                  content: t('user_setting.2fa.success.confirm.content'),
                  confirmBoxProps: {
                    confirmButtonText: t('user_setting.2fa.success.confirm.ok'),
                    cancelText: t('common.cancel'),
                    onOk: () => {
                      setStep(STEP.CODE)
                    },
                  },
                })
              )
            }}
          />
        </SettingItem>
      )}

      {step == STEP.TURN_OFF_SUCCESS && (
        <div className="UserSetting">
          <div className="UserSetting__success">
            <img
              className="UserSetting__success__img"
              src="/images/success_check.png"
              alt="success"
            />

            <h3 className="UserSetting__h3">
              {t('user_setting.2fa.success_off.h3')}
            </h3>

            <p className="UserSetting__p">
              {t('user_setting.2fa.success_off.p')}
            </p>
          </div>
        </div>
      )}
    </React.Fragment>
  )
}

export default withSettingModal(TwoFaSetting)

const ButtonWrapper = ({
  onClick,
  disabled,
  isLoading,
}: {
  onClick?: React.MouseEventHandler
  disabled?: boolean
  isLoading?: boolean
}) => {
  const { t } = useTranslation()

  return (
    <div className="UserSetting__form">
      <Button
        className="UserSetting__form__submit"
        size="medium"
        type="submit"
        onClick={onClick}
        disabled={disabled}
      >
        {isLoading ? t('common.loading') : t('common.next')}
      </Button>
    </div>
  )
}

const Copy = ({ codes }) => {
  const { t } = useTranslation()

  const [__isActive, __isActiveSet] = React.useState(false)

  React.useEffect(() => {
    if (__isActive) {
      window.setTimeout(() => {
        __isActiveSet(false)
      }, 800)
    }
  }, [__isActive])

  return (
    <a
      className="UserSetting__success__buttons__a"
      onClick={() => {
        window?.navigator?.clipboard?.writeText?.(codes?.join?.(', ') ?? '')

        __isActiveSet(true)
      }}
    >
      {t(`copy_to_clipboard.${__isActive ? 'copied' : 'copy'}`)}
    </a>
  )
}
